import {
  ImportModuleAdminEventGroup,
  ImportModuleHistoryEventGroup,
  ImportModuleProfile,
  ImportModuleProfilePath,
  ImportModuleStoredDecision,
  ImportModuleUnresolvedId,
} from '../../core/definitions/import-module';
import {saveBlob} from './csv-download';
import type {GroupKind, NamedObjectType} from './group-card/group-card.model';
import {GROUP_ID_KINDS, GroupIdKind, PLACEMENT_EVENT_KIND} from './import-payload';

/**
 * Carrying a profile to another database as a file: reading a file as a
 * profile, blanking the database ids in it that name no record where it
 * lands, and naming the file it is saved to.
 */

/** The profile fields that each hold one database id. */
export type BlankedPickKey = 'collection_id' | 'reg_level_id' | 'status_id';

/** Where a blanked id stood, for the page to name:
 * - `pick`: a profile field of `BlankedPickKey`, with the label the file
 *   holds for its record;
 * - `mapping`: a mapping row, or a row onto a field of the linked media,
 *   by its target field;
 * - `batch`: a value for all rows, by its batch key;
 * - `card`: a group card's pick. `position` counts the card among the
 *   restored cards of its kind, and is `null` when it is the only one or
 *   gets no card. `subBlock` is the Named or Place sub-block it sits on,
 *   with its position the same way. `field` is the header key on the page,
 *   or `type_id` (a Named sub-block's type, of `objectType`) or
 *   `create_missing_parent_id`;
 * - `decision`: the parent of the new term a value decision creates, by
 *   the decision's field and source value;
 * - `unknown`: a path the page has no place for. */
export type BlankedPlace =
  | {kind: 'pick'; key: BlankedPickKey; label: string | null}
  | {kind: 'mapping'; targetField: string}
  | {kind: 'batch'; key: string}
  | {kind: 'card'; card: GroupKind; position: number | null; subBlock: BlankedSubBlock | null; field: string;
     objectType?: NamedObjectType}
  | {kind: 'decision'; field: string; value: string}
  | {kind: 'unknown'};

/** The sub-block of a history-event card a blanked id sat on. */
export interface BlankedSubBlock {
  kind: 'named' | 'place';
  position: number | null;
}

/** A database id blanked out of a carried profile, with where it stood. */
export interface BlankedId {
  id: string;
  place: BlankedPlace;
}

/** A profile with its unresolved ids taken out. */
export interface CarriedProfile {
  profile: ImportModuleProfile;
  // The ids blanked in place, in the order given. A value decision dropped
  // whole is not among them.
  blanked: BlankedId[];
  droppedDecisions: number;
}

/** The text of a profile file as a profile; `null` when it is not JSON or
 * not the object a saved profile is. Only the keys a restore and a carry
 * read are checked: the backend refuses a profile it cannot read. */
export function parseProfileFile(text: string): ImportModuleProfile | null {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return null;
  }
  return isProfile(data) ? data : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isText(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

function isProfile(data: unknown): data is ImportModuleProfile {
  return isRecord(data)
    && isText(data['profile_id']) && isText(data['profile_name']) && typeof data['object_type'] === 'string'
    && Array.isArray(data['mapping'])
    && (data['value_decisions'] === undefined || Array.isArray(data['value_decisions']))
    && (data['batch'] === undefined || isRecord(data['batch']));
}

/** Keys a path may not step through. */
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * `profile` without the ids in `unresolved`, which name no record in the
 * database it is carried to. A value decision whose `target_artifact_id` is
 * unresolved is dropped; every other id is blanked where its path says,
 * to `null`, and to empty text for the `value` of a fixed pick. A decision
 * kept with a blanked `new_concept_parent_id` is a decision that creates
 * its term at the root. A path that leads nowhere in the profile is
 * skipped. `profile` itself is left as it is.
 */
export function carryProfile(profile: ImportModuleProfile, unresolved: ImportModuleUnresolvedId[]): CarriedProfile {
  const carried: ImportModuleProfile = JSON.parse(JSON.stringify(profile));
  const dropped = new Set<number>();
  for (const {path} of unresolved) {
    if (path.length === 3 && path[0] === 'value_decisions' && path[2] === 'target_artifact_id') {
      dropped.add(path[1] as number);
    }
  }

  const blanked: BlankedId[] = [];
  for (const {id, path} of unresolved) {
    if (path[0] === 'value_decisions' && dropped.has(path[1] as number)) {
      continue;
    }
    const place = blankedPlace(profile, path);
    if (blankAt(carried, path)) {
      blanked.push({id, place});
    }
  }

  const decisions = carried.value_decisions ?? [];
  const kept = decisions.filter((_, index) => !dropped.has(index));
  if (carried.value_decisions) {
    carried.value_decisions = kept;
  }
  return {profile: carried, blanked, droppedDecisions: decisions.length - kept.length};
}

/** Blanks the value at `path` of `root`; whether it stood there. */
function blankAt(root: unknown, path: ImportModuleProfilePath): boolean {
  if (!path.length || path.some(step => typeof step === 'string' && UNSAFE_KEYS.has(step))) {
    return false;
  }
  let node: unknown = root;
  for (const step of path.slice(0, -1)) {
    if (typeof node !== 'object' || node === null || !Object.hasOwn(node, step)) {
      return false;
    }
    node = (node as Record<string | number, unknown>)[step];
  }
  const leaf = path[path.length - 1];
  if (typeof node !== 'object' || node === null || !Object.hasOwn(node, leaf)) {
    return false;
  }
  (node as Record<string | number, unknown>)[leaf] = leaf === 'value' ? '' : null;
  return true;
}

/** The value at `path` of `root`, `undefined` where nothing stands. */
function valueAt(root: unknown, path: ImportModuleProfilePath): unknown {
  let node = root;
  for (const step of path) {
    if (typeof node !== 'object' || node === null || !Object.hasOwn(node, step)) {
      return undefined;
    }
    node = (node as Record<string | number, unknown>)[step];
  }
  return node;
}

const PICK_KEYS: readonly string[] = ['collection_id', 'reg_level_id', 'status_id'] satisfies BlankedPickKey[];

/** The header key on an admin-event card of each id key its group holds. */
const ADMIN_EVENT_HEADER_KEYS: Readonly<Record<string, string>> = {
  sub_type_id: 'admin_event_sub_type_id',
  purpose_type_id: 'admin_event_purpose_type_id',
};

const UNKNOWN: BlankedPlace = {kind: 'unknown'};

/** Where the id at `path` of `profile` stands (`BlankedPlace`). */
function blankedPlace(profile: ImportModuleProfile, path: ImportModuleProfilePath): BlankedPlace {
  const [head, index] = path;
  if (path.length === 1 && PICK_KEYS.includes(String(head))) {
    const label = valueAt(profile, [String(head).replace(/_id$/, '_label')]);
    return {kind: 'pick', key: head as BlankedPickKey, label: isText(label) ? label : null};
  }
  if (path.length === 2 && head === 'batch' && typeof index === 'string') {
    return {kind: 'batch', key: index};
  }
  const mappingEntryPath = head === 'mapping' ? path.slice(0, 2) : head === 'media' && path[1] === 'mapping'
    ? path.slice(0, 3) : null;
  if (mappingEntryPath) {
    const targetField = valueAt(profile, [...mappingEntryPath, 'target_field']);
    return typeof targetField === 'string' ? {kind: 'mapping', targetField} : UNKNOWN;
  }
  if (head === 'value_decisions' && path[2] === 'new_concept_parent_id') {
    const decision = valueAt(profile, ['value_decisions', index]) as ImportModuleStoredDecision | undefined;
    return isRecord(decision) ? {kind: 'decision', field: decision.source_column, value: decision.source_value}
      : UNKNOWN;
  }
  if (typeof index !== 'number') {
    return UNKNOWN;
  }
  if (head === 'history_event_groups') {
    return historyEventPlace(profile.history_event_groups ?? [], index, path.slice(2));
  }
  if (head === 'admin_event_groups' && path.length === 3) {
    return adminEventPlace(profile.admin_event_groups ?? [], index, String(path[2]));
  }
  const groupIdKind = GROUP_ID_KINDS.find(kind => head === `${kind}_groups`);
  if (groupIdKind && path.length === 3) {
    return groupIdPlace(profile, groupIdKind, index, String(path[2]));
  }
  return UNKNOWN;
}

/** `position` + 1 among `count` cards or sub-blocks, `null` for the only
 * one. */
function ordinal(position: number, count: number): number | null {
  return count > 1 ? position + 1 : null;
}

/** The place of `rest`, a path into the `index`th history-event group:
 * its header pick, or a pick on one of its sub-blocks. Restore gives each
 * group a card, in list order, and each sub-block a block, in list order. */
function historyEventPlace(
  groups: ImportModuleHistoryEventGroup[], index: number, rest: ImportModuleProfilePath,
): BlankedPlace {
  const group = groups[index];
  if (!isRecord(group)) {
    return UNKNOWN;
  }
  const card = {kind: 'card', card: 'history_event', position: ordinal(index, groups.length)} as const;
  if (rest.length === 1) {
    return {...card, subBlock: null, field: String(rest[0])};
  }
  const [list, subIndex, field] = rest;
  if (rest.length !== 3 || (list !== 'named' && list !== 'places') || typeof subIndex !== 'number') {
    return UNKNOWN;
  }
  const subBlocks: unknown[] = group[list] ?? [];
  const subBlock = subBlocks[subIndex];
  if (!isRecord(subBlock)) {
    return UNKNOWN;
  }
  return {
    ...card,
    subBlock: {kind: list === 'named' ? 'named' : 'place', position: ordinal(subIndex, subBlocks.length)},
    field: String(field),
    ...(list === 'named' && field === 'type_id' ? {objectType: subBlock['object_type'] as NamedObjectType} : {}),
  };
}

/** The place of the `wireKey` pick of the `index`th admin-event group,
 * whose card counts among the groups of its own `admin_event_type`. */
function adminEventPlace(groups: ImportModuleAdminEventGroup[], index: number, wireKey: string): BlankedPlace {
  const group = groups[index];
  if (!isRecord(group)) {
    return UNKNOWN;
  }
  const field = ADMIN_EVENT_HEADER_KEYS[wireKey];
  const sameType = groups.filter(g => isRecord(g) && g.admin_event_type === group.admin_event_type);
  return field ? {
    kind: 'card', card: group.admin_event_type === PLACEMENT_EVENT_KIND ? 'placement_event' : 'admin_event',
    position: ordinal(sameType.indexOf(group), sameType.length), subBlock: null, field,
  } : UNKNOWN;
}

/** The place of the `field` pick of the `index`th group of `kind`.
 * Restore gives a card only to a group some mapping entry names in its
 * `group_id`, and orders the cards by the first entry naming each. */
function groupIdPlace(profile: ImportModuleProfile, kind: GroupIdKind, index: number, field: string): BlankedPlace {
  const groups: unknown[] = profile[`${kind}_groups`] ?? [];
  const group = groups[index];
  if (!isRecord(group)) {
    return UNKNOWN;
  }
  const firstUse = new Map<string, number>();
  profile.mapping.forEach((entry, position) => {
    if (isRecord(entry) && entry.group_id && !firstUse.has(entry.group_id)) {
      firstUse.set(entry.group_id, position);
    }
  });
  const cards = groups
    .map(g => isRecord(g) ? g['key'] : undefined)
    .filter((key): key is string => typeof key === 'string' && firstUse.has(key))
    .sort((a, b) => firstUse.get(a)! - firstUse.get(b)!);
  const position = cards.indexOf(group['key'] as string);
  return {
    kind: 'card', card: kind, position: position < 0 ? null : ordinal(position, cards.length), subBlock: null, field,
  };
}

/** The name of the file a profile called `name` is saved to. */
export function profileFileName(name: string): string {
  // eslint-disable-next-line no-control-regex
  const safe = name.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_').trim();
  return `${safe || 'profile'}.json`;
}

/** Saves `profile` as the JSON file of its name. */
export function downloadProfileFile(profile: ImportModuleProfile): void {
  saveBlob(new Blob([JSON.stringify(profile, null, 2)], {type: 'application/json'}), profileFileName(profile.profile_name));
}
